import { config } from "@utils/config";
import {
  createContext,
  Dispatch,
  ReactChild,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { getInitialTheme } from "./themeContext";

type Props = {
  initialTheme?: any;
  children: ReactChild[] | ReactChild;
};

interface ContextData {
  theme: string;
  setTheme: Dispatch<SetStateAction<string>>;
  language: string;
  setLanguage: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isSettingsOpen: boolean;
  setIsSettingsOpen: Dispatch<SetStateAction<boolean>>;
  construction: boolean;
  setConstruction: Dispatch<SetStateAction<boolean>>;
}

const ContextDefaultValue: ContextData = {
  theme: getInitialTheme(),
  setTheme:
    () =>
    (_value: SetStateAction<string>): void => {
      throw new Error("Function not implemented.");
    },
  language: "",
  setLanguage: (_value: SetStateAction<string>): void => {
    throw new Error("Function not implemented.");
  },
  isLoading: true,
  setIsLoading: (_value: SetStateAction<boolean>): void => {
    throw new Error("Function not implemented.");
  },
  isSettingsOpen: false,
  setIsSettingsOpen: (_value: SetStateAction<boolean>): void => {
    throw new Error("Function not implemented.");
  },
  construction: true,
  setConstruction: (_value: SetStateAction<boolean>): void => {
    throw new Error("Function not implemented.");
  },
};

export const AppContext = createContext<ContextData>(ContextDefaultValue);

const AppContextProvider = ({ initialTheme, children }: Props) => {
  const [theme, setTheme] = useState(ContextDefaultValue.theme);
  const [construction, setConstruction] = useState(
    ContextDefaultValue.construction
  );
  const [language, setLanguage] = useState(ContextDefaultValue.language);
  const [isLoading, setIsLoading] = useState(ContextDefaultValue.isLoading);
  const [isSettingsOpen, setIsSettingsOpen] = useState(
    ContextDefaultValue.isSettingsOpen
  );

  useEffect(() => {
    if (!language) return;

    localStorage.setItem(config.localStorage.language, language);
  }, [language]);

  const rawSetTheme = (theme: string): void => {
    const root = window.document.documentElement;
    const isDark = theme === "dark";

    root.classList.remove(isDark ? "light" : "dark");
    root.classList.add(theme);

    localStorage.setItem(config.localStorage.theme, theme);
  };

  if (initialTheme) {
    rawSetTheme(initialTheme);
  }

  useEffect(() => {
    if (!theme) return;
    rawSetTheme(theme);
  }, [theme]);

  return (
    <AppContext.Provider
      value={{
        theme,
        setTheme,
        language,
        setLanguage,
        isLoading,
        setIsLoading,
        isSettingsOpen,
        setIsSettingsOpen,
        construction,
        setConstruction,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
